import Avatar from '@/components/linear/avatar';
import PriorityIcon from '@/components/linear/priority-icon';
import StatusIcon from '@/components/linear/status-icon';
import { useWorkspaceChrome } from '@/components/linear/workspace-chrome';
import {
  issueAssigneePath,
  issuePath,
  issuePriorityPath,
  issueStatusPath,
} from '@/lib/linear-paths';
import {
  ACTIVE_STATUSES,
  ISSUE_STATUSES,
  STATUS_LABELS,
  groupIssues,
  type IssueGroupBy,
} from '@/lib/linear-utils';
import type { Issue, IssueStatus } from '@/types/issue';
import { router } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Props {
  issues: Issue[];
  selectedId?: string | null;
  onSelect?: (issue: Issue) => void;
  view?: 'all' | 'active' | 'backlog';
  groupBy?: IssueGroupBy;
}

export default function IssueBoard({
  issues,
  selectedId,
  onSelect,
  view = 'active',
  groupBy = 'status',
}: Props) {
  const { selectedIndex, items, setSelectedIndex, setCreateIssueOpen, registerItems } =
    useWorkspaceChrome();
  const [dropKey, setDropKey] = useState<string | null>(null);

  useEffect(() => {
    registerItems(
      issues.map((issue) => ({
        id: issue.id,
        identifier: issue.identifier,
        issue,
      }))
    );
    return () => registerItems([]);
  }, [issues, registerItems]);

  const statusColumns: IssueStatus[] =
    view === 'backlog' ? ['backlog'] : view === 'all' ? ISSUE_STATUSES : ACTIVE_STATUSES;
  const columns =
    groupBy === 'status'
      ? statusColumns.map((status) => ({
          key: status,
          label: STATUS_LABELS[status],
          issues: issues.filter((issue) => issue.status === status),
        }))
      : groupIssues(issues, groupBy);

  return (
    <div className="ln-board">
      {columns.map((column) => (
        <section
          key={column.key}
          className="ln-board-col"
          data-drop={dropKey === column.key ? 'true' : 'false'}
          onDragOver={(event) => {
            event.preventDefault();
            setDropKey(column.key);
          }}
          onDragLeave={() => {
            if (dropKey === column.key) setDropKey(null);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDropKey(null);
            const issueId = event.dataTransfer.getData('text/issue-id');
            if (!issueId) return;
            applyBoardDrop(issueId, groupBy, column.key);
          }}
        >
          <div className="ln-board-col-header">
            {groupBy === 'status' ? <StatusIcon status={column.key as IssueStatus} /> : null}
            <span>{column.label}</span>
            <span className="ln-group-count">{column.issues.length}</span>
            <button
              type="button"
              className="ln-group-add"
              aria-label={`Create ${column.label} issue`}
              onClick={() => setCreateIssueOpen(true)}
            >
              <Plus size={14} />
            </button>
          </div>
          <div className="ln-board-col-body">
            {column.issues.map((issue) => {
              const itemIndex = items.findIndex((item) => item.id === issue.id);
              const selected = selectedId
                ? selectedId === issue.id || selectedId === issue.identifier
                : itemIndex === selectedIndex;

              return (
                <button
                  key={issue.id}
                  type="button"
                  className="ln-board-card"
                  data-selected={selected ? 'true' : 'false'}
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/issue-id', issue.id);
                    event.dataTransfer.effectAllowed = 'move';
                  }}
                  onClick={() => {
                    if (itemIndex >= 0) setSelectedIndex(itemIndex);
                    if (onSelect) onSelect(issue);
                  }}
                >
                  <div className="ln-board-card-top">
                    <PriorityIcon priority={issue.priority} />
                    <span className="ln-row-id">{issue.identifier}</span>
                    {issue.assignee ? (
                      <span style={{ marginLeft: 'auto' }}>
                        <Avatar
                          name={issue.assignee.name}
                          color={issue.assignee.avatarColor}
                          size={16}
                        />
                      </span>
                    ) : null}
                  </div>
                  <div className="ln-board-card-title">{issue.title}</div>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function applyBoardDrop(issueId: string, groupBy: IssueGroupBy, key: string) {
  if (groupBy === 'status') {
    router.patch(issueStatusPath(issueId), { status: key }, { preserveScroll: true });
  } else if (groupBy === 'assignee') {
    router.patch(
      issueAssigneePath(issueId),
      { assigneeId: key === 'unassigned' ? null : key },
      { preserveScroll: true }
    );
  } else if (groupBy === 'priority') {
    router.patch(issuePriorityPath(issueId), { priority: Number(key) }, { preserveScroll: true });
  } else if (groupBy === 'project') {
    router.patch(
      issuePath(issueId),
      { issue: { projectId: key === 'none' ? null : key } },
      { preserveScroll: true }
    );
  }
}
